<template>
  <div class="data-board">
    <WelcomeBanner title="数据看板">
      <template #right>
        <el-button type="primary" size="small" @click="refreshData" class="refresh-btn">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
      </template>
    </WelcomeBanner>

    <!-- 统计数据卡片 -->
    <el-row :gutter="16" class="stats-row">
      <el-col :xs="12" :sm="8" :lg="4" v-for="card in statsCards" :key="card.key">
        <StatCard :value="card.value" :label="card.label" :icon="card.icon" :gradient="card.gradient" :show-percent="false" />
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="16" class="charts-row">
      <!-- 任务完成趋势 -->
      <el-col :xs="24" :lg="12">
        <div class="chart-panel">
          <div class="panel-header">
            <div class="panel-title">
              <i class="el-icon-s-data"></i>
              <span>任务完成趋势</span>
            </div>
            <span class="panel-sub">近7天</span>
          </div>
          <div class="chart-body">
            <div ref="taskTrendChart" class="chart"></div>
          </div>
        </div>
      </el-col>

      <!-- 机器人活跃度排行 -->
      <el-col :xs="24" :lg="12">
        <div class="chart-panel">
          <div class="panel-header">
            <div class="panel-title">
              <i class="el-icon-trophy"></i>
              <span>机器人活跃度</span>
            </div>
            <span class="panel-sub">TOP10</span>
          </div>
          <div class="chart-body">
            <div ref="robotActivityChart" class="chart"></div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDataBoardStats, getTaskTrend, getRobotActivity } from '../api/dashboard'
import StatCard from '@/components/StatCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'DataBoard',
  components: { StatCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      statsCards: [
        { key: 'totalTasks', label: '累计任务', value: '0', icon: 'el-icon-document', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', trend: '0%', trendType: 'flat' },
        { key: 'completedTasks', label: '已完成', value: '0', icon: 'el-icon-circle-check', gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', trend: '0%', trendType: 'flat' },
        { key: 'totalRobots', label: '接入机器人', value: '0', icon: 'el-icon-user', gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', trend: '0%', trendType: 'flat' },
        { key: 'totalProjects', label: '协作项目', value: '0', icon: 'el-icon-folder-opened', gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', trend: '0%', trendType: 'flat' },
        { key: 'pendingTasks', label: '进行中', value: '0', icon: 'el-icon-loading', gradient: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)', trend: '0%', trendType: 'flat' },
        { key: 'todayCompleted', label: '今日完成', value: '0', icon: 'el-icon-check', gradient: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', trend: '0%', trendType: 'flat' }
      ],
      taskTrendChart: null,
      robotActivityChart: null
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    if (this.taskTrendChart) this.taskTrendChart.dispose()
    if (this.robotActivityChart) this.robotActivityChart.dispose()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [statsData, trendData, activityData] = await Promise.all([
          getDataBoardStats(),
          getTaskTrend(7),
          getRobotActivity()
        ])
        if (statsData) {
          const stats = {
            totalTasks: { value: statsData.user_tasks || 0, trend: 0 },
            completedTasks: { value: statsData.completed_tasks || 0, trend: 0 },
            totalRobots: { value: statsData.user_robots || 0, trend: 0 },
            totalProjects: { value: statsData.user_projects || 0, trend: 0 },
            pendingTasks: { value: statsData.pending_tasks || 0, trend: 0 },
            todayCompleted: { value: statsData.today_completed || 0, trend: 0 }
          }
          this.processStatsData(stats)
        }
        if (trendData) {
          this.initTaskTrendChart({ dates: trendData.dates || [], values: trendData.completed || [] })
        }
        if (activityData && activityData.robots) {
          const robotRanking = activityData.robots.map(robot => ({ name: robot.robot_name, value: robot.completed_tasks }))
          this.initRobotActivityChart(robotRanking)
        }
      } catch (error) {
        console.error('加载数据看板数据失败', error)
        this.initMockData()
      } finally {
        this.loading = false
      }
    },
    refreshData() { this.loadData() },
    processStatsData(stats) {
      this.statsCards.forEach(card => {
        if (stats[card.key] !== undefined) {
          card.value = this.formatNumber(stats[card.key].value)
          card.trend = `${stats[card.key].trend > 0 ? '+' : ''}${stats[card.key].trend}%`
          card.trendType = stats[card.key].trend > 0 ? 'up' : stats[card.key].trend < 0 ? 'down' : 'flat'
        }
      })
    },
    formatNumber(num) {
      return num >= 10000 ? (num / 10000).toFixed(1) + 'w' : num.toString()
    },
    initTaskTrendChart(data) {
      if (!this.taskTrendChart) this.taskTrendChart = echarts.init(this.$refs.taskTrendChart)
      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          textStyle: { color: '#606266', fontSize: 12 },
          padding: [10, 14],
          formatter: function(params) {
            const data = params[0]
            return `<div style="font-weight:600">${data.axisValue}</div>
                    <div style="margin-top:6px;color:#6366f1">完成任务: <span style="font-weight:600;font-size:14px">${data.value}</span> 个</div>`
          }
        },
        grid: { left: '3%', right: '4%', bottom: '8%', top: '10%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: data?.dates || ['', '', '', '', '', '', ''],
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#c0c4cc', fontSize: 11, margin: 10 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#c0c4cc', fontSize: 11 }
        },
        series: [{
          name: '完成任务数',
          type: 'line',
          smooth: 0.6,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { color: '#6366f1', width: 3, shadowColor: 'rgba(99, 102, 241, 0.3)', shadowBlur: 10 },
          itemStyle: { color: '#fff', borderColor: '#6366f1', borderWidth: 2 },
          emphasis: { scale: 1.5, itemStyle: { shadowBlur: 15, shadowColor: 'rgba(99, 102, 241, 0.5)' } },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(99, 102, 241, 0.25)' },
              { offset: 0.5, color: 'rgba(99, 102, 241, 0.1)' },
              { offset: 1, color: 'rgba(99, 102, 241, 0.01)' }
            ])
          },
          data: data?.values || [0, 0, 0, 0, 0, 0, 0]
        }]
      }
      this.taskTrendChart.setOption(option)
    },
    initRobotActivityChart(data) {
      if (!this.robotActivityChart) this.robotActivityChart = echarts.init(this.$refs.robotActivityChart)
      let robotNames = [], activityValues = []
      if (data?.length > 0) {
        const top10 = data.slice(0, 10).reverse()
        robotNames = top10.map(item => item.name)
        activityValues = top10.map(item => item.value)
      }
      const maxValue = Math.max(...activityValues, 1)
      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          textStyle: { color: '#606266', fontSize: 12 },
          padding: [10, 14],
          formatter: function(params) {
            const data = params[0]
            return `<div style="font-weight:600">${data.name}</div>
                    <div style="margin-top:6px;color:#8b5cf6">完成任务: <span style="font-weight:600;font-size:14px">${data.value}</span> 个</div>`
          }
        },
        grid: { left: '3%', right: '8%', bottom: '3%', top: '5%', containLabel: true },
        xAxis: {
          type: 'value',
          max: Math.ceil(maxValue * 1.15),
          splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#c0c4cc', fontSize: 11 }
        },
        yAxis: {
          type: 'category',
          data: robotNames,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#909399', fontSize: 11, width: 70, overflow: 'truncate' }
        },
        series: [{
          name: '任务完成量',
          type: 'bar',
          data: activityValues,
          barWidth: '55%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#a78bfa' },
              { offset: 0.5, color: '#8b5cf6' },
              { offset: 1, color: '#6366f1' }
            ]),
            borderRadius: [0, 6, 6, 0]
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#c4b5fd' },
                { offset: 0.5, color: '#a78bfa' },
                { offset: 1, color: '#8b5cf6' }
              ])
            }
          },
          showBackground: true,
          backgroundStyle: { color: 'rgba(245, 247, 250, 0.5)', borderRadius: [0, 6, 6, 0] }
        }]
      }
      this.robotActivityChart.setOption(option)
    },
    resizeCharts() {
      if (this.taskTrendChart) this.taskTrendChart.resize()
      if (this.robotActivityChart) this.robotActivityChart.resize()
    },
    initMockData() {
      const mockStats = {
        totalTasks: { value: 1256, trend: 12.5 }, completedTasks: { value: 987, trend: 8.3 },
        totalRobots: { value: 24, trend: 20 }, totalProjects: { value: 36, trend: 5.2 },
        pendingTasks: { value: 42, trend: -3.2 }, todayCompleted: { value: 35, trend: 12 }
      }
      this.processStatsData(mockStats)
      const dates = [], values = [], today = new Date()
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000)
        dates.push(`${date.getMonth() + 1}/${date.getDate()}`)
        values.push(Math.floor(Math.random() * 50) + 20)
      }
      this.initTaskTrendChart({ dates, values })
      this.initRobotActivityChart([
        { name: '产品需求分析师', value: 156 }, { name: '前端开发工程师', value: 142 },
        { name: '后端开发工程师', value: 138 }, { name: 'UI设计师', value: 125 },
        { name: '测试工程师', value: 118 }, { name: '运维工程师', value: 95 },
        { name: '数据分析师', value: 87 }, { name: '安全工程师', value: 76 },
        { name: '技术文档工程师', value: 68 }, { name: '项目经理', value: 62 }
      ])
    }
  }
}
</script>

<style lang="scss" scoped>
.data-board {
  padding: 16px;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  min-height: calc(100vh - 100px);
}

/* 欢迎横幅 */
.welcome-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);
}

.banner-sub {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  i {
    margin-right: 4px;
  }
}

/* 统计卡片 */
.stats-row {
  margin-bottom: 16px;
}


/* 图表区域 */
.charts-row {
  margin-bottom: 16px;
  
  &::before, &::after { display: none !important; }
  
  > .el-col {
    display: block;
    padding: 0 8px;
    
    &:first-child { padding-left: 0; }
    &:last-child { padding-right: 0; }
  }
}

.chart-panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #909399;

  i { color: #909399; font-size: 14px; }
}

.panel-sub {
  font-size: 11px;
  color: #c0c4cc;
}

.chart-body {
  padding: 12px 16px 16px;
}

.chart {
  width: 100%;
  height: 280px;
}

/* 响应式 */
@media screen and (max-width: 1200px) {
  .charts-row > .el-col { padding: 0 !important; }
}

@media screen and (max-width: 768px) {
  .data-board { padding: 12px; }
  .charts-row { flex-direction: column; }
  .chart { height: 240px; }
}
</style>
